import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// Generate or retrieve a persistent visitor ID
const getVisitorId = (): string => {
  const storageKey = "visitor_id";
  let visitorId = localStorage.getItem(storageKey);
  
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem(storageKey, visitorId);
  }
  
  return visitorId;
};

// Fetch country info from a free geo-location API
const fetchGeoLocation = async (): Promise<{
  country?: string;
  country_code?: string;
  city?: string;
  region?: string;
}> => {
  try {
    const response = await fetch("https://ipapi.co/json/", { 
      cache: "force-cache" 
    });
    if (response.ok) {
      const data = await response.json();
      return {
        country: data.country_name,
        country_code: data.country_code,
        city: data.city,
        region: data.region,
      };
    }
  } catch (error) {
    console.error("Failed to fetch geo location:", error);
  }
  return {};
};

export function useVisitorTracking() {
  const location = useLocation();
  const trackedPaths = useRef<Set<string>>(new Set());
  const sessionId = useRef<string>(`session_${Date.now()}`);

  useEffect(() => {
    const trackVisit = async () => {
      const pathKey = `${sessionId.current}_${location.pathname}`;
      
      // Don't track the same page twice in the same session
      if (trackedPaths.current.has(pathKey)) {
        return;
      }
      
      trackedPaths.current.add(pathKey);

      const visitorId = getVisitorId();
      const geoData = await fetchGeoLocation();

      try {
        await supabase.from("visitor_analytics").insert({
          visitor_id: visitorId,
          page_path: location.pathname,
          country: geoData.country || null,
          country_code: geoData.country_code || null,
          city: geoData.city || null,
          region: geoData.region || null,
          user_agent: navigator.userAgent,
          referrer: document.referrer || null,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
        });
      } catch (error) {
        console.error("Failed to track visit:", error);
      }
    };

    trackVisit();
  }, [location.pathname]);
}
